import posthog from "posthog-js";

class AnalyticsService {
  // ==================== USER IDENTIFICATION ====================

  identify(userUUID, traits = {}) {
    if (!import.meta.env.VITE_POSTHOG_KEY) return;
    posthog.identify(userUUID, traits);
  }

  /**
   * Fires once when a brand-new account is created (NOT on returning logins).
   * The funnel endpoint for the §9.5 canary gate
   * (anonymous_transcription_completed → signed_up). Source-tool attribution
   * is read off the person's $initial_utm_* props (the landing tool page),
   * which carry over now that the anonymous identity is shared across
   * .audioscribe.io. Caller is responsible for the new-account + dedupe guard.
   */
  signedUp(props = {}) {
    this.track("signed_up", props);
  }

  setWorkspaceGroup(workspaceUUID, traits = {}) {
    if (!import.meta.env.VITE_POSTHOG_KEY) return;
    posthog.group("workspace", workspaceUUID, traits);
  }

  reset() {
    if (!import.meta.env.VITE_POSTHOG_KEY) return;
    posthog.reset();
  }

  // ==================== FEATURE FLAGS ====================

  /**
   * Check if a feature flag is enabled for the current user.
   * @param {string} flagKey - The PostHog feature flag key
   * @param {boolean} defaultValue - Value to return if PostHog is unavailable or flag is undefined (default: true)
   * @returns {boolean} Whether the feature is enabled
   */
  isFeatureEnabled(flagKey, defaultValue = true) {
    if (!import.meta.env.VITE_POSTHOG_KEY) return defaultValue;
    return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
  }

  // ==================== CORE TRACKING ====================

  // Analytics must never be able to break the feature it measures. posthog-js
  // `capture()` has no internal try/catch (only `identify` gets the library's
  // safe wrapper), so a throw here would propagate into callers — several of
  // which sit inside the recording lifecycle, in try blocks whose catch
  // re-throws. A failed capture is swallowed and warned instead.
  track(event, properties = {}) {
    if (!import.meta.env.VITE_POSTHOG_KEY) return;
    try {
      posthog.capture(event, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.warn(`[Analytics] capture failed for "${event}":`, error);
    }
  }

  // ==================== NAVIGATION ====================

  workspaceDropdownOpened() {
    this.track("workspace_dropdown_opened");
  }
  workspaceDropdownClosed() {
    this.track("workspace_dropdown_closed");
  }
  workspaceSelected(props) {
    this.track("workspace_selected", props);
  }
  addWorkspaceClicked(props) {
    this.track("add_workspace_clicked", props);
  }
  workspaceLimitUpgradeClicked(props) {
    this.track("workspace_limit_upgrade_clicked", props);
  }

  navHomeClicked(props) {
    this.track("nav_home_clicked", props);
  }
  navConversationsClicked(props) {
    this.track("nav_conversations_clicked", props);
  }
  navFavoritesClicked(props) {
    this.track("nav_favorites_clicked", props);
  }
  navSettingsClicked(props) {
    this.track("nav_settings_clicked", props);
  }
  navLogoutClicked() {
    this.track("nav_logout_clicked");
  }
  navUpgradeClicked(props) {
    this.track("nav_upgrade_clicked", props);
  }
  navHelpClicked() {
    this.track("nav_help_clicked");
  }

  // ==================== TOP BAR SEARCH ====================

  searchFocused() {
    this.track("search_focused");
  }
  searchSubmitted(props) {
    this.track("search_submitted", props);
  }
  searchResultsDisplayed(props) {
    this.track("search_results_displayed", props);
  }
  searchResultClicked(props) {
    this.track("search_result_clicked", props);
  }
  searchCleared(props) {
    this.track("search_cleared", props);
  }
  searchNoResults(props) {
    this.track("search_no_results", props);
  }

  // ==================== TOP BAR BUTTONS ====================

  importButtonClicked() {
    this.track("import_button_clicked");
  }
  shareButtonClicked(props) {
    this.track("share_button_clicked", props);
  }
  exportButtonClicked(props) {
    this.track("export_button_clicked", props);
  }

  // ==================== HOME VIEW ====================

  homeViewed() {
    this.track("home_viewed");
  }
  homeImportClicked() {
    this.track("home_import_clicked");
  }

  // ==================== WELCOME VIDEO ====================

  /** Fires when the user actually starts playback (not on modal open). */
  welcomeVideoPlayed(props) {
    this.track("welcome_video_played", props);
  }
  /**
   * Fires once per page session when the playback POSITION passes 90% of
   * duration. Scrubbing to the end counts — this measures reaching the end,
   * not minutes actually watched.
   */
  welcomeVideoCompleted(props) {
    this.track("welcome_video_completed", props);
  }

  // ==================== ONBOARDING CHECKLIST ====================

  onboardingChecklistViewed(props) {
    this.track("onboarding_checklist_viewed", props);
  }
  onboardingStepCompleted(props) {
    this.track("onboarding_step_completed", props);
  }
  onboardingChecklistDismissed(props) {
    this.track("onboarding_checklist_dismissed", props);
  }

  // ==================== CONVERSATIONS ====================

  conversationsViewed(props) {
    this.track("conversations_viewed", props);
  }
  conversationsRefreshed(props) {
    this.track("conversations_refreshed", props);
  }

  fileCardClicked(props) {
    this.track("file_card_clicked", props);
  }
  fileCardHovered(props) {
    this.track("file_card_hovered", props);
  }
  fileFavoriteToggled(props) {
    this.track("file_favorite_toggled", props);
  }
  fileOptionsOpened(props) {
    this.track("file_options_opened", props);
  }
  fileDownloadMediaClicked(props) {
    this.track("file_download_media_clicked", props);
  }
  fileDownloadTranscriptClicked(props) {
    this.track("file_download_transcript_clicked", props);
  }
  fileDeleteClicked(props) {
    this.track("file_delete_clicked", props);
  }

  // ==================== TAGS ====================

  tagCreated(props) {
    this.track("tag_created", props);
  }
  tagAttached(props) {
    this.track("tag_attached", props);
  }
  tagDetached(props) {
    this.track("tag_detached", props);
  }

  // ==================== FAVORITES ====================

  favoritesViewed(props) {
    this.track("favorites_viewed", props);
  }

  // ==================== TRANSCRIPTION - LEFT SIDE ====================

  transcriptionViewed(props) {
    this.track("transcription_viewed", props);
  }
  leftTabTranscriptClicked(props) {
    this.track("left_tab_transcript_clicked", props);
  }
  leftTabSummaryClicked(props) {
    this.track("left_tab_summary_clicked", props);
  }
  leftTabAiChatClicked(props) {
    this.track("left_tab_ai_chat_clicked", props);
  }

  // Transcript Search
  transcriptSearchFocused(props) {
    this.track("transcript_search_focused", props);
  }
  transcriptSearchSubmitted(props) {
    this.track("transcript_search_submitted", props);
  }
  transcriptSearchNextClicked(props) {
    this.track("transcript_search_next_clicked", props);
  }
  transcriptSearchPrevClicked(props) {
    this.track("transcript_search_prev_clicked", props);
  }
  transcriptSearchCleared(props) {
    this.track("transcript_search_cleared", props);
  }

  // Edit Mode
  editModeEntered(props) {
    this.track("edit_mode_entered", props);
  }
  editModeExited(props) {
    this.track("edit_mode_exited", props);
  }

  // Transcript Interactions
  transcriptWordClicked(props) {
    this.track("transcript_word_clicked", props);
  }
  transcriptWordEditClicked(props) {
    this.track("transcript_word_edit_clicked", props);
  }
  transcriptWordEdited(props) {
    this.track("transcript_word_edited", props);
  }
  transcriptWordEditCancelled(props) {
    this.track("transcript_word_edit_cancelled", props);
  }
  transcriptTextSelected(props) {
    this.track("transcript_text_selected", props);
  }
  transcriptScrolled(props) {
    this.track("transcript_scrolled", props);
  }
  transcriptReturnToPositionClicked(props) {
    this.track("transcript_return_to_position_clicked", props);
  }

  // Speaker Interactions
  speakerLabelClicked(props) {
    this.track("speaker_label_clicked", props);
  }
  speakerDropdownOpened(props) {
    this.track("speaker_dropdown_opened", props);
  }
  speakerSelected(props) {
    this.track("speaker_selected", props);
  }
  speakerScopeThisBlockSelected(props) {
    this.track("speaker_scope_this_block_selected", props);
  }
  speakerScopeAllBlocksSelected(props) {
    this.track("speaker_scope_all_blocks_selected", props);
  }
  speakerUpdateConfirmed(props) {
    this.track("speaker_update_confirmed", props);
  }
  speakerUpdateCancelled(props) {
    this.track("speaker_update_cancelled", props);
  }

  // Text Selection Actions
  selectionPopupShown(props) {
    this.track("selection_popup_shown", props);
  }
  selectionReassignSpeakerClicked(props) {
    this.track("selection_reassign_speaker_clicked", props);
  }
  selectionHighlightClicked(props) {
    this.track("selection_highlight_clicked", props);
  }
  selectionCommentClicked(props) {
    this.track("selection_comment_clicked", props);
  }

  // Highlights & Comments
  highlightCreated(props) {
    this.track("highlight_created", props);
  }
  commentInputOpened(props) {
    this.track("comment_input_opened", props);
  }
  commentCreated(props) {
    this.track("comment_created", props);
  }
  commentCancelled(props) {
    this.track("comment_cancelled", props);
  }

  // Summary Tab
  summaryViewed(props) {
    this.track("summary_viewed", props);
  }
  summaryRegenerateClicked(props) {
    this.track("summary_regenerate_clicked", props);
  }
  summaryRegenerated(props) {
    this.track("summary_regenerated", props);
  }

  // AI Chat Tab
  aiChatViewed(props) {
    this.track("ai_chat_viewed", props);
  }
  aiChatInputFocused(props) {
    this.track("ai_chat_input_focused", props);
  }
  aiChatMessageSent(props) {
    this.track("ai_chat_message_sent", props);
  }
  aiChatResponseReceived(props) {
    this.track("ai_chat_response_received", props);
  }
  aiChatHistoryScrolled(props) {
    this.track("ai_chat_history_scrolled", props);
  }
  aiChatSuggestionClicked(props) {
    this.track("ai_chat_suggestion_clicked", props);
  }
  aiChatSuggestionsShown(props) {
    this.track("ai_chat_suggestions_shown", props);
  }

  // ==================== TRANSCRIPTION - RIGHT SIDE ====================

  // Media Player
  mediaPlayerLoaded(props) {
    this.track("media_player_loaded", props);
  }
  mediaPlayClicked(props) {
    this.track("media_play_clicked", props);
  }
  mediaPauseClicked(props) {
    this.track("media_pause_clicked", props);
  }
  mediaSeeked(props) {
    this.track("media_seeked", props);
  }
  mediaVolumeChanged(props) {
    this.track("media_volume_changed", props);
  }
  mediaSpeedChanged(props) {
    this.track("media_speed_changed", props);
  }
  mediaFullscreenEntered(props) {
    this.track("media_fullscreen_entered", props);
  }
  mediaFullscreenExited(props) {
    this.track("media_fullscreen_exited", props);
  }
  mediaEnded(props) {
    this.track("media_ended", props);
  }
  mediaProgress25(props) {
    this.track("media_progress_25", props);
  }
  mediaProgress50(props) {
    this.track("media_progress_50", props);
  }
  mediaProgress75(props) {
    this.track("media_progress_75", props);
  }
  mediaProgress100(props) {
    this.track("media_progress_100", props);
  }

  // Utility Tabs
  rightTabTimelineClicked(props) {
    this.track("right_tab_timeline_clicked", props);
  }
  rightTabCommentsClicked(props) {
    this.track("right_tab_comments_clicked", props);
  }
  rightTabExportClicked(props) {
    this.track("right_tab_export_clicked", props);
  }
  rightTabAboutClicked(props) {
    this.track("right_tab_about_clicked", props);
  }

  // Timeline Tab
  timelineViewed(props) {
    this.track("timeline_viewed", props);
  }
  timelineSegmentClicked(props) {
    this.track("timeline_segment_clicked", props);
  }
  timelineSpeakerPlayClicked(props) {
    this.track("timeline_speaker_play_clicked", props);
  }
  timelineFilteredPlaybackStarted(props) {
    this.track("timeline_filtered_playback_started", props);
  }
  timelineFilteredPlaybackStopped(props) {
    this.track("timeline_filtered_playback_stopped", props);
  }

  // Comments Tab
  commentsTabViewed(props) {
    this.track("comments_tab_viewed", props);
  }
  commentHighlightClicked(props) {
    this.track("comment_highlight_clicked", props);
  }
  commentOptionsOpened(props) {
    this.track("comment_options_opened", props);
  }
  commentEditClicked(props) {
    this.track("comment_edit_clicked", props);
  }
  commentEdited(props) {
    this.track("comment_edited", props);
  }
  commentDeleteClicked(props) {
    this.track("comment_delete_clicked", props);
  }
  commentDeleted(props) {
    this.track("comment_deleted", props);
  }
  commentReplyClicked(props) {
    this.track("comment_reply_clicked", props);
  }
  commentReplySubmitted(props) {
    this.track("comment_reply_submitted", props);
  }

  // Export Tab
  exportTabViewed(props) {
    this.track("export_tab_viewed", props);
  }
  exportTranscriptTxtClicked(props) {
    this.track("export_transcript_txt_clicked", props);
  }
  exportTranscriptTxtCompleted(props) {
    this.track("export_transcript_txt_completed", props);
  }
  exportTranscriptSrtClicked(props) {
    this.track("export_transcript_srt_clicked", props);
  }
  exportTranscriptSrtCompleted(props) {
    this.track("export_transcript_srt_completed", props);
  }
  exportTranscriptVttClicked(props) {
    this.track("export_transcript_vtt_clicked", props);
  }
  exportTranscriptVttCompleted(props) {
    this.track("export_transcript_vtt_completed", props);
  }
  exportSummaryClicked(props) {
    this.track("export_summary_clicked", props);
  }
  exportSummaryCompleted(props) {
    this.track("export_summary_completed", props);
  }
  exportChatHistoryClicked(props) {
    this.track("export_chat_history_clicked", props);
  }
  exportChatHistoryCompleted(props) {
    this.track("export_chat_history_completed", props);
  }
  exportMediaClicked(props) {
    this.track("export_media_clicked", props);
  }
  exportMediaCompleted(props) {
    this.track("export_media_completed", props);
  }

  // About Tab
  aboutTabViewed(props) {
    this.track("about_tab_viewed", props);
  }

  // ==================== POPUPS ====================

  // Import Popup
  importPopupOpened(props) {
    this.track("import_popup_opened", props);
  }
  importPopupClosed() {
    this.track("import_popup_closed");
  }
  importLanguageChanged(props) {
    this.track("import_language_changed", props);
  }
  importFileSelected(props) {
    this.track("import_file_selected", props);
  }
  importFileRemoved(props) {
    this.track("import_file_removed", props);
  }
  importUploadStarted(props) {
    this.track("import_upload_started", props);
  }
  importUploadProgress(props) {
    this.track("import_upload_progress", props);
  }
  importUploadCompleted(props) {
    this.track("import_upload_completed", props);
  }
  importUploadFailed(props) {
    this.track("import_upload_failed", props);
  }
  importLimitReachedShown(props) {
    this.track("import_limit_reached_shown", props);
  }
  importUpgradeClicked(props) {
    this.track("import_upgrade_clicked", props);
  }

  // Export Popup
  exportPopupOpened(props) {
    this.track("export_popup_opened", props);
  }
  exportPopupClosed() {
    this.track("export_popup_closed");
  }
  exportPopupTxtClicked(props) {
    this.track("export_popup_txt_clicked", props);
  }
  exportPopupVttClicked(props) {
    this.track("export_popup_vtt_clicked", props);
  }
  exportPopupSrtClicked(props) {
    this.track("export_popup_srt_clicked", props);
  }
  exportPopupMediaClicked(props) {
    this.track("export_popup_media_clicked", props);
  }

  // Share Popup
  sharePopupOpened(props) {
    this.track("share_popup_opened", props);
  }
  sharePopupClosed() {
    this.track("share_popup_closed");
  }
  shareCreateClicked(props) {
    this.track("share_create_clicked", props);
  }
  shareLinkCreated(props) {
    this.track("share_link_created", props);
  }
  shareExpirationToggled(props) {
    this.track("share_expiration_toggled", props);
  }
  shareExpirationSet(props) {
    this.track("share_expiration_set", props);
  }
  shareLinkCopied(props) {
    this.track("share_link_copied", props);
  }
  shareLinkDeactivated(props) {
    this.track("share_link_deactivated", props);
  }

  // Delete File Popup
  deleteFilePopupOpened(props) {
    this.track("delete_file_popup_opened", props);
  }
  deleteFilePopupClosed(props) {
    this.track("delete_file_popup_closed", props);
  }
  deleteFileConfirmed(props) {
    this.track("delete_file_confirmed", props);
  }

  // New Workspace Popup
  newWorkspacePopupOpened(props) {
    this.track("new_workspace_popup_opened", props);
  }
  newWorkspacePopupClosed() {
    this.track("new_workspace_popup_closed");
  }
  newWorkspaceNameEntered(props) {
    this.track("new_workspace_name_entered", props);
  }
  newWorkspaceCreated(props) {
    this.track("new_workspace_created", props);
  }
  newWorkspaceError(props) {
    this.track("new_workspace_error", props);
  }

  // Word Editor Popup
  wordEditorOpened(props) {
    this.track("word_editor_opened", props);
  }
  wordEditorClosed(props) {
    this.track("word_editor_closed", props);
  }

  // ==================== SETTINGS ====================

  settingsProfileViewed() {
    this.track("settings_profile_viewed");
  }
  settingsPlansViewed(props) {
    this.track("settings_plans_viewed", props);
  }
  settingsBillingViewed(props) {
    this.track("settings_billing_viewed", props);
  }
  settingsWorkspacesViewed(props) {
    this.track("settings_workspaces_viewed", props);
  }
  settingsConnectedAppsViewed(props) {
    this.track("settings_connected_apps_viewed", props);
  }

  profileFirstNameEdited() {
    this.track("profile_first_name_edited");
  }
  profileLastNameEdited() {
    this.track("profile_last_name_edited");
  }
  profileSaveClicked() {
    this.track("profile_save_clicked");
  }
  profileSaved() {
    this.track("profile_saved");
  }
  profileDeleteAccountClicked() {
    this.track("profile_delete_account_clicked");
  }

  plansBillingCycleToggled(props) {
    this.track("plans_billing_cycle_toggled", props);
  }
  plansFreePlanViewed() {
    this.track("plans_free_plan_viewed");
  }
  plansStandardPlanViewed() {
    this.track("plans_standard_plan_viewed");
  }
  plansEnterprisePlanViewed() {
    this.track("plans_enterprise_plan_viewed");
  }
  plansUpgradeClicked(props) {
    this.track("plans_upgrade_clicked", props);
  }
  plansDowngradeClicked(props) {
    this.track("plans_downgrade_clicked", props);
  }
  plansContactSalesClicked() {
    this.track("plans_contact_sales_clicked");
  }

  billingManageSubscriptionClicked(props) {
    this.track("billing_manage_subscription_clicked", props);
  }
  billingPortalOpened() {
    this.track("billing_portal_opened");
  }

  connectedAppsHelpClicked(props) {
    this.track("connected_apps_help_clicked", props);
  }
  connectedAppDisconnectClicked(props) {
    this.track("connected_app_disconnect_clicked", props);
  }
  connectedAppDisconnected(props) {
    this.track("connected_app_disconnected", props);
  }
  connectedAppDisconnectFailed(props) {
    this.track("connected_app_disconnect_failed", props);
  }

  workspaceSettingsRenameClicked(props) {
    this.track("workspace_settings_rename_clicked", props);
  }
  workspaceSettingsRenamed(props) {
    this.track("workspace_settings_renamed", props);
  }
  workspaceSettingsDeleteClicked(props) {
    this.track("workspace_settings_delete_clicked", props);
  }
  workspaceSettingsDeleted(props) {
    this.track("workspace_settings_deleted", props);
  }
  workspaceSettingsInviteClicked(props) {
    this.track("workspace_settings_invite_clicked", props);
  }
  workspaceSettingsMemberInvited(props) {
    this.track("workspace_settings_member_invited", props);
  }
  workspaceSettingsMemberRemoved(props) {
    this.track("workspace_settings_member_removed", props);
  }
  workspaceSettingsMemberRoleChanged(props) {
    this.track("workspace_settings_member_role_changed", props);
  }

  deleteAccountPopupOpened() {
    this.track("delete_account_popup_opened");
  }
  deleteAccountPopupClosed() {
    this.track("delete_account_popup_closed");
  }
  deleteAccountConfirmed(props) {
    this.track("delete_account_confirmed", props);
  }

  // ==================== ONBOARDING ====================

  onboardingStarted() {
    this.track("onboarding_started");
  }

  // Step 1: Primary Purpose
  onboardingStep1Viewed() {
    this.track("onboarding_step_1_viewed");
  }
  onboardingPurposeSelected(props) {
    this.track("onboarding_purpose_selected", props);
  }
  onboardingStep1Completed(props) {
    this.track("onboarding_step_1_completed", props);
  }

  // Step 2: Recording Length
  onboardingStep2Viewed() {
    this.track("onboarding_step_2_viewed");
  }
  onboardingRecordingLengthToggled(props) {
    this.track("onboarding_recording_length_toggled", props);
  }
  onboardingStep2Completed(props) {
    this.track("onboarding_step_2_completed", props);
  }

  // Step 3: Attribution
  onboardingStep3Viewed() {
    this.track("onboarding_step_3_viewed");
  }
  onboardingAttributionSelected(props) {
    this.track("onboarding_attribution_selected", props);
  }
  onboardingStep3Completed(props) {
    this.track("onboarding_step_3_completed", props);
  }

  // Step 4: Current Tool
  onboardingStep4Viewed() {
    this.track("onboarding_step_4_viewed");
  }
  onboardingCurrentToolSelected(props) {
    this.track("onboarding_current_tool_selected", props);
  }
  onboardingStep4Completed(props) {
    this.track("onboarding_step_4_completed", props);
  }

  // Completion
  onboardingCompleted(props) {
    this.track("onboarding_completed", props);
  }
  onboardingSkipped(props) {
    this.track("onboarding_skipped", props);
  }

  // ==================== PAYMENT ====================

  checkoutInitiated(props) {
    this.track("checkout_initiated", props);
  }
  checkoutCancelled(props) {
    this.track("checkout_cancelled", props);
  }
  paymentSuccessViewed() {
    this.track("payment_success_viewed");
  }
  paymentCancelViewed() {
    this.track("payment_cancel_viewed");
  }

  // ==================== PUBLIC SHARE ====================

  publicShareViewed(props) {
    this.track("public_share_viewed", props);
  }
  publicShareExpiredShown(props) {
    this.track("public_share_expired_shown", props);
  }
  publicShareTranscriptViewed(props) {
    this.track("public_share_transcript_viewed", props);
  }
  publicShareSummaryViewed(props) {
    this.track("public_share_summary_viewed", props);
  }
  publicShareMediaPlayed(props) {
    this.track("public_share_media_played", props);
  }
  publicShareChatSigninClicked(props) {
    this.track("public_share_chat_signin_clicked", props);
  }
  publicShareDownloadClicked(props) {
    this.track("public_share_download_clicked", props);
  }
  publicShareSignupClicked(props) {
    this.track("public_share_signup_clicked", props);
  }
  publicShareSignupCompleted(props) {
    this.track("public_share_signup_completed", props);
  }

  // ==================== KEYBOARD SHORTCUTS ====================

  keyboardSearchTriggered(props) {
    this.track("keyboard_search_triggered", props);
  }
  keyboardPlayPauseTriggered(props) {
    this.track("keyboard_play_pause_triggered", props);
  }
  keyboardEscapePressed(props) {
    this.track("keyboard_escape_pressed", props);
  }

  // ==================== BROWSER NOTIFICATIONS ====================

  notificationPromptShown() {
    this.track("notification_prompt_shown");
  }
  notificationPromptAccepted() {
    this.track("notification_prompt_accepted");
  }
  notificationPromptDismissed(props) {
    this.track("notification_prompt_dismissed", props);
  }
  notificationPermissionGranted() {
    this.track("notification_permission_granted");
  }
  notificationPermissionDenied() {
    this.track("notification_permission_denied");
  }
  notificationClicked(props) {
    this.track("notification_clicked", props);
  }
  notificationSettingsToggled(props) {
    this.track("notification_settings_toggled", props);
  }

  // ==================== FEATURE TOUR ====================

  featureTourOffered(props) {
    this.track("feature_tour_offered", props);
  }
  featureTourStarted(props) {
    this.track("feature_tour_started", props);
  }
  featureTourStepViewed(props) {
    this.track("feature_tour_step_viewed", props);
  }
  featureTourDismissed(props) {
    this.track("feature_tour_dismissed", props);
  }
  featureTourCompleted(props) {
    this.track("feature_tour_completed", props);
  }

  // ==================== MOBILE SHELL ====================

  mobileShellViewed(props) {
    this.track("mobile_shell_viewed", props);
  }
  mobileTabClicked(props) {
    this.track("mobile_tab_clicked", props);
  }
  mobileLibraryChipClicked(props) {
    this.track("mobile_library_chip_clicked", props);
  }
  mobileRecordSheetOpened() {
    this.track("mobile_record_sheet_opened");
  }
  mobilePlayerUsed(props) {
    this.track("mobile_player_used", props);
  }
  appBannerShown(props) {
    this.track("app_banner_shown", props);
  }
  appBannerClicked(props) {
    this.track("app_banner_clicked", props);
  }
  appBannerDismissed(props) {
    this.track("app_banner_dismissed", props);
  }
  appInstallSheetShown(props) {
    this.track("app_install_sheet_shown", props);
  }
  appInstallSheetClicked(props) {
    this.track("app_install_sheet_clicked", props);
  }
  appInstallSheetDismissed(props) {
    this.track("app_install_sheet_dismissed", props);
  }

  // ==================== ERRORS ====================

  errorApiFailed(props) {
    this.track("error_api_failed", props);
  }
  errorUploadFailed(props) {
    this.track("error_upload_failed", props);
  }
  errorExportFailed(props) {
    this.track("error_export_failed", props);
  }
  errorWordEditFailed(props) {
    this.track("error_word_edit_failed", props);
  }
  errorShareCreateFailed(props) {
    this.track("error_share_create_failed", props);
  }

  // ==================== LIMITS (Critical for Propensity) ====================

  limitFileUploadReached(props) {
    this.track("limit_file_upload_reached", props);
  }
  limitFileUploadWarning(props) {
    this.track("limit_file_upload_warning", props);
  }
  limitWorkspaceReached(props) {
    this.track("limit_workspace_reached", props);
  }
  limitUpgradePromptShown(props) {
    this.track("limit_upgrade_prompt_shown", props);
  }
  limitUpgradePromptClicked(props) {
    this.track("limit_upgrade_prompt_clicked", props);
  }
  limitUpgradePromptDismissed(props) {
    this.track("limit_upgrade_prompt_dismissed", props);
  }

  // ==================== ADVANCED EXPORT MODAL ====================
  // Called through the modal's trackQuietly() string indirection, so a
  // missing method used to fail silently — analytics.contract.spec.js now
  // fails the test run instead.

  advancedExportOpened(props) {
    this.track("advanced_export_opened", props);
  }
  advancedExportClosed(props) {
    this.track("advanced_export_closed", props);
  }
  advancedExportCompleted(props) {
    this.track("advanced_export_completed", props);
  }
  advancedExportFailed(props) {
    this.track("advanced_export_failed", props);
  }

  // ==================== SPEAKER RENAME (label → popover / mobile sheet) ====================
  // Event names match the Android app so cross-platform dashboards line up.

  speakerRenameOpened(props) {
    this.track("speaker_rename_opened", props);
  }
  speakerRenamed(props) {
    this.track("speaker_renamed", props);
  }
  speakerRenameFailed(props) {
    this.track("speaker_rename_failed", props);
  }

  // ==================== ACCOUNT DELETION ====================

  /** The API refused or failed a deletion; the popup keeps the user signed in. */
  accountDeletionFailed(props) {
    this.track("account_deletion_failed", props);
  }
}

export const analytics = new AnalyticsService();

/**
 * Single source of truth for the real-time-recording gate. The flag string
 * must appear only here — desktop TopBar and the mobile shell all consume
 * this helper.
 */
export function isRecordingEnabled() {
  return analytics.isFeatureEnabled("audio-recording", false);
}
